import { formatWholeNumber } from '../util/numberFormat';

const plural = (value, singular, pluralForm) => value === 1 ? singular : pluralForm;
const digitCount = value => Math.max(1, String(Math.abs(value)).length);
const placeNames = [
  'unidades',
  'decenas',
  'centenas',
  'millares',
  'decenas de millar',
  'centenas de millar',
  'millones',
];
const singularPlaceNames = [
  'unidad',
  'decena',
  'centena',
  'millar',
  'decena de millar',
  'centena de millar',
  'millón',
];

function resultOf(problem) {
  return Number.isFinite(problem?.answer) ? problem.answer : problem.a * problem.b;
}

function buildEqualGroupsTutorial(problem) {
  const a = Math.max(0, Math.trunc(Number(problem?.a) || 0));
  const b = Math.max(0, Math.trunc(Number(problem?.b) || 0));
  const result = resultOf({ ...problem, a, b });
  const repeatedAddends = a > 0
    ? Array.from({ length: a }, () => formatWholeNumber(b)).join(' + ')
    : '';

  return {
    kind: 'equal-groups',
    a,
    b,
    result,
    repeatedAddends,
    groups: Array.from({ length: a }, (_, index) => ({
      number: index + 1,
      crystals: Array.from({ length: b }, (_, crystalIndex) => crystalIndex + 1),
    })),
    steps: [
      {
        id: 'groups',
        label: 'Paso 1',
        title: `Forma ${formatWholeNumber(a)} ${plural(a, 'grupo', 'grupos')} de ${formatWholeNumber(b)}.`,
        message: a === 0
          ? 'No hay grupos que formar.'
          : 'Cada grupo se representa con gemas de energía.',
      },
      {
        id: 'each-group',
        label: 'Paso 2',
        title: a === 0
          ? 'No hay grupos.'
          : `Cada grupo tiene ${formatWholeNumber(b)}.`,
        message: a === 0
          ? 'Como formamos 0 grupos, no hay gemas que contar.'
          : 'Todos los grupos tienen la misma cantidad de gemas.',
      },
      {
        id: 'repeated-addition',
        label: 'Paso 3',
        title: 'Sumamos los grupos.',
        message: a === 0
          ? 'No hay grupos que sumar porque formamos 0 grupos.'
          : `Sumamos ${formatWholeNumber(a)} ${plural(a, 'grupo', 'grupos')} de ${formatWholeNumber(b)}.`,
        showRepeatedAddition: true,
      },
      {
        id: 'result',
        label: 'Resultado',
        title: `Entonces, ${formatWholeNumber(a)} × ${formatWholeNumber(b)} = ${formatWholeNumber(result)}.`,
        message: a === 0
          ? 'Cero grupos de cualquier cantidad dan 0.'
          : 'La suma repetida nos da el resultado de la multiplicación.',
        showRepeatedAddition: true,
        showProduct: true,
      },
    ],
  };
}

function regroupMessage(value, position) {
  const nextPlace = placeNames[position + 1] || 'unidades del siguiente valor posicional';
  const singularNextPlace = singularPlaceNames[position + 1] || 'unidad del siguiente valor posicional';
  return `Reagrupamos ${formatWholeNumber(value)} ${plural(value, singularNextPlace, nextPlace)}.`;
}

function buildPlanetTwoColumnMultiplicationTutorial(problem) {
  const a = Math.max(0, Math.trunc(Number(problem?.a) || 0));
  const b = Math.max(0, Math.trunc(Number(problem?.b) || 0));
  const result = resultOf({ ...problem, a, b });
  const digits = String(a).split('').map(Number);
  const gridWidth = Math.max(digitCount(result), digitCount(a) + 1);
  const multiplierColumn = gridWidth - 1;
  const resultVisible = [];
  const steps = [{
    id: 'align',
    label: 'Paso 1',
    title: 'Alineamos los factores.',
    message: 'Empezamos por las unidades y avanzamos hacia la izquierda.',
    activeTopColumn: null,
    activeMultiplierColumn: null,
    carries: [],
    resultVisible: [],
  }];

  let carry = 0;
  for (let sourceIndex = digits.length - 1, position = 0; sourceIndex >= 0; sourceIndex -= 1, position += 1) {
    const digit = digits[sourceIndex];
    const product = digit * b;
    const total = product + carry;
    const resultDigit = total % 10;
    const nextCarry = Math.floor(total / 10);
    const gridColumn = gridWidth - 1 - position;
    const nextCarryEntries = nextCarry && gridColumn > 0
      ? [{ gridColumn: gridColumn - 1, value: nextCarry }]
      : [];
    const place = placeNames[position] || 'valor posicional';
    const placeLabel = place[0].toUpperCase() + place.slice(1);
    const calculation = `${formatWholeNumber(digit)} × ${formatWholeNumber(b)} = ${formatWholeNumber(product)}.`;
    const carrySentence = carry
      ? ` Sumamos ${formatWholeNumber(carry)} y obtenemos ${formatWholeNumber(total)}.`
      : '';
    const writeMessage = `Escribimos ${formatWholeNumber(resultDigit)} en ${place}.`;
    resultVisible.push(gridColumn);

    steps.push({
      id: `column-${position}`,
      label: `Paso ${steps.length + 1}`,
      title: `${placeLabel}: ${calculation}${carrySentence}`,
      message: nextCarry ? `${writeMessage} ${regroupMessage(nextCarry, position)}` : writeMessage,
      activeTopColumn: gridColumn,
      activeMultiplierColumn: multiplierColumn,
      // Solo queda a la vista el reagrupamiento que se usará en la próxima columna.
      carries: nextCarryEntries,
      resultVisible: [...resultVisible],
    });
    carry = nextCarry;
  }

  steps.push({
    id: 'result',
    label: 'Resultado',
    title: `Entonces, ${formatWholeNumber(a)} × ${formatWholeNumber(b)} = ${formatWholeNumber(result)}.`,
    message: 'Leemos todas las cifras del resultado.',
    activeTopColumn: null,
    activeMultiplierColumn: null,
    carries: [],
    resultVisible: Array.from({ length: gridWidth }, (_, index) => index),
  });

  return {
    kind: 'planet-two-columns-multiplication',
    a,
    b,
    result,
    gridWidth,
    steps,
  };
}

function buildColumnMultiplicationTutorial(problem) {
  const a = Math.max(0, Math.trunc(Number(problem?.a) || 0));
  const b = Math.max(0, Math.trunc(Number(problem?.b) || 0));
  const result = resultOf({ ...problem, a, b });
  const digits = String(a).split('').map(Number);
  const gridWidth = Math.max(digitCount(result), digitCount(a) + 1);
  const multiplierColumn = gridWidth - 1;
  const resultVisible = [];
  const steps = [{
    id: 'align',
    label: 'Paso 1',
    title: 'Alineamos los factores.',
    message: 'Empezamos por las unidades y avanzamos hacia la izquierda.',
    activeTopColumn: null,
    activeMultiplierColumn: null,
    carries: [],
    resultVisible: [],
  }];

  let carry = 0;
  for (let sourceIndex = digits.length - 1, position = 0; sourceIndex >= 0; sourceIndex -= 1, position += 1) {
    const digit = digits[sourceIndex];
    const product = digit * b;
    const total = product + carry;
    const resultDigit = total % 10;
    const nextCarry = Math.floor(total / 10);
    const gridColumn = gridWidth - 1 - position;
    const nextCarryEntries = nextCarry && gridColumn > 0
      ? [{ gridColumn: gridColumn - 1, value: nextCarry }]
      : [];
    const place = placeNames[position] || 'valor posicional';
    const placeLabel = place[0].toUpperCase() + place.slice(1);
    const calculation = `${formatWholeNumber(digit)} × ${formatWholeNumber(b)} = ${formatWholeNumber(product)}.`;
    const carrySentence = carry
      ? ` Sumamos ${formatWholeNumber(carry)} y obtenemos ${formatWholeNumber(total)}.`
      : '';
    const writeMessage = `Escribimos ${formatWholeNumber(resultDigit)} en ${place}.`;
    resultVisible.push(gridColumn);

    steps.push({
      id: `column-${position}`,
      label: `Paso ${steps.length + 1}`,
      title: `${placeLabel}: ${calculation}${carrySentence}`,
      message: nextCarry ? `${writeMessage} ${regroupMessage(nextCarry, position)}` : writeMessage,
      activeTopColumn: gridColumn,
      activeMultiplierColumn: multiplierColumn,
      // Tras usar una reagrupación, queda visible solamente la que alimenta
      // la siguiente columna. Así nunca aparece un número flotando.
      carries: nextCarryEntries,
      resultVisible: [...resultVisible],
    });
    carry = nextCarry;
  }

  steps.push({
    id: 'result',
    label: 'Resultado',
    title: `Entonces, ${formatWholeNumber(a)} × ${formatWholeNumber(b)} = ${formatWholeNumber(result)}.`,
    message: 'Leemos todas las cifras del resultado.',
    activeTopColumn: null,
    activeMultiplierColumn: null,
    carries: [],
    resultVisible: Array.from({ length: gridWidth }, (_, index) => index),
  });

  return {
    kind: 'columns-multiplication',
    a,
    b,
    result,
    gridWidth,
    steps,
  };
}

function regroupShiftedMessage(value, position) {
  const targetPosition = position + 2;
  const nextPlace = placeNames[targetPosition] || 'unidades del siguiente valor posicional';
  const singularNextPlace = singularPlaceNames[targetPosition] || 'unidad del siguiente valor posicional';
  return `Reagrupamos ${formatWholeNumber(value)} ${plural(value, singularNextPlace, nextPlace)}.`;
}

function buildPlanetFivePartialProductsTutorial(problem) {
  const a = Math.max(0, Math.trunc(Number(problem?.a) || 0));
  const b = Math.max(0, Math.trunc(Number(problem?.b) || 0));
  const result = resultOf({ ...problem, a, b });
  const digits = String(a).split('').map(Number);
  const units = b % 10;
  const tens = Math.floor(b / 10) % 10;
  const firstPartial = a * units;
  const secondPartialBase = a * tens;
  const secondPartialValue = secondPartialBase * 10;
  const gridWidth = Math.max(4, digitCount(result), digitCount(firstPartial), digitCount(secondPartialValue));
  const unitsColumn = gridWidth - 1;
  const tensColumn = gridWidth - 2;
  const firstVisible = [];
  const secondVisible = [];
  // Conservan ceros escritos durante un paso intermedio aunque el número final
  // se reduzca a una sola cifra, como ocurre al calcular 20 × 0.
  const firstWrittenZeroColumns = [];
  const secondWrittenZeroColumns = [];
  const steps = [{
    id: 'align',
    label: 'Paso 1',
    title: 'Alineamos los factores.',
    message: 'Alineamos los factores por valor posicional.',
    activeTopColumn: null,
    activeMultiplierColumn: null,
    carries: [],
  }];

  // Primer producto parcial: multiplicamos por las unidades del segundo factor.
  let carry = 0;
  for (let sourceIndex = digits.length - 1, position = 0; sourceIndex >= 0; sourceIndex -= 1, position += 1) {
    const digit = digits[sourceIndex];
    const product = digit * units;
    const total = product + carry;
    const resultDigit = total % 10;
    const nextCarry = Math.floor(total / 10);
    const topColumn = gridWidth - 1 - position;
    const nextCarryEntries = nextCarry && topColumn > 0
      ? [{ gridColumn: topColumn - 1, value: nextCarry }]
      : [];
    const place = placeNames[position];
    const placeLabel = place[0].toUpperCase() + place.slice(1);
    const carriedPlace = plural(carry, singularPlaceNames[position], placeNames[position]);
    const carryArticle = carry === 1 ? 'la' : 'las';
    const carryAdjective = carry === 1 ? 'reagrupada' : 'reagrupadas';
    const carrySentence = carry
      ? ` Sumamos ${carryArticle} ${formatWholeNumber(carry)} ${carriedPlace} ${carryAdjective} y obtenemos ${formatWholeNumber(total)}.`
      : '';
    firstVisible.push(topColumn);
    if (resultDigit === 0) firstWrittenZeroColumns.push(topColumn);

    steps.push({
      id: `units-${position}`,
      label: `Paso ${steps.length + 1}`,
      title: `${placeLabel} del primer producto parcial: ${formatWholeNumber(digit)} × ${formatWholeNumber(units)} = ${formatWholeNumber(product)}.${carrySentence}`,
      message: nextCarry
        ? `Escribimos ${formatWholeNumber(resultDigit)} en ${place}. ${regroupMessage(nextCarry, position)}`
        : `Escribimos ${formatWholeNumber(resultDigit)} en ${place}.`,
      activeTopColumn: topColumn,
      activeMultiplierColumn: unitsColumn,
      carries: nextCarryEntries,
      firstVisible: [...firstVisible],
      firstWrittenZeroColumns: [...firstWrittenZeroColumns],
    });
    carry = nextCarry;
  }

  // Si el primer producto parcial es 0, conservamos los ceros escritos por
  // columnas (por ejemplo, 20 × 0 se ve como 00 durante la suma vertical).
  const finalFirstZeroColumns = firstPartial === 0 ? [...firstWrittenZeroColumns] : [];

  steps.push({
    id: 'first-partial',
    label: `Paso ${steps.length + 1}`,
    title: 'Primer producto parcial.',
    message: `${formatWholeNumber(a)} × ${formatWholeNumber(units)} = ${formatWholeNumber(firstPartial)}.`,
    activeTopColumn: null,
    activeMultiplierColumn: null,
    carries: [],
    showFirstPartial: true,
      firstWrittenZeroColumns: finalFirstZeroColumns,
  });

  // El segundo factor se interpreta como DECENAS desde el primer momento.
  steps.push({
    id: 'introduce-tens',
    label: `Paso ${steps.length + 1}`,
    title: `Ahora multiplicamos por ${formatWholeNumber(tens)} ${plural(tens, 'decena', 'decenas')}.`,
    message: `El ${formatWholeNumber(tens)} representa ${formatWholeNumber(tens)} ${plural(tens, 'decena', 'decenas')}, por eso este producto parcial comienza en la columna de las decenas.`,
    activeTopColumn: null,
    activeMultiplierColumn: tensColumn,
    carries: [],
    showFirstPartial: true,
      firstWrittenZeroColumns: finalFirstZeroColumns,
  });

  // Segundo producto parcial: cada cifra se explica con su valor posicional.
  carry = 0;
  for (let sourceIndex = digits.length - 1, position = 0; sourceIndex >= 0; sourceIndex -= 1, position += 1) {
    const digit = digits[sourceIndex];
    const product = digit * tens;
    const total = product + carry;
    const resultDigit = total % 10;
    const nextCarry = Math.floor(total / 10);
    const topColumn = gridWidth - 1 - position;
    const resultColumn = topColumn - 1;
    const nextCarryEntries = nextCarry && topColumn > 0
      ? [{ gridColumn: topColumn - 1, value: nextCarry }]
      : [];
    const sourcePlace = placeNames[position];
    const resultPlace = placeNames[position + 1] || 'siguiente valor posicional';
    const sourcePlaceSingular = singularPlaceNames[position] || 'unidad';
    const resultPlaceSingular = singularPlaceNames[position + 1] || 'unidad';
    const carriedPlace = plural(carry, resultPlaceSingular, resultPlace);
    const carryArticle = carry === 1 ? 'la' : 'las';
    const carryAdjective = carry === 1 ? 'reagrupada' : 'reagrupadas';
    const carrySentence = carry
      ? ` Sumamos ${carryArticle} ${formatWholeNumber(carry)} ${carriedPlace} ${carryAdjective} y obtenemos ${formatWholeNumber(total)} ${plural(total, resultPlaceSingular, resultPlace)}.`
      : '';
    secondVisible.push(resultColumn);
    if (resultDigit === 0) secondWrittenZeroColumns.push(resultColumn);

    steps.push({
      id: `tens-${position}`,
      label: `Paso ${steps.length + 1}`,
      title: `${formatWholeNumber(tens)} ${plural(tens, 'decena', 'decenas')} × ${formatWholeNumber(digit)} ${plural(digit, sourcePlaceSingular, sourcePlace)} = ${formatWholeNumber(product)} ${plural(product, resultPlaceSingular, resultPlace)}.${carrySentence}`,
      message: nextCarry
        ? `Escribimos ${formatWholeNumber(resultDigit)} en ${resultPlace}. ${regroupShiftedMessage(nextCarry, position)}`
        : `Escribimos ${formatWholeNumber(resultDigit)} en ${resultPlace}.`,
      activeTopColumn: topColumn,
      activeMultiplierColumn: tensColumn,
      carries: nextCarryEntries,
      showFirstPartial: true,
      firstWrittenZeroColumns: finalFirstZeroColumns,
      secondVisible: [...secondVisible],
      secondWrittenZeroColumns: [...secondWrittenZeroColumns],
    });
    carry = nextCarry;
  }

  steps.push({
    id: 'second-partial',
    label: `Paso ${steps.length + 1}`,
    title: 'Segundo producto parcial.',
    message: `${formatWholeNumber(a)} × ${formatWholeNumber(tens)} ${plural(tens, 'decena', 'decenas')} = ${formatWholeNumber(secondPartialValue)}.`,
    activeTopColumn: null,
    activeMultiplierColumn: null,
    carries: [],
    showFirstPartial: true,
      firstWrittenZeroColumns: finalFirstZeroColumns,
    showSecondPartial: true,
  });

  // La suma se construye también por columnas; nunca se revela de golpe.
  const digitAt = (value, position) => Math.floor(value / (10 ** position)) % 10;
  const sumColumnCount = Math.max(digitCount(firstPartial), digitCount(secondPartialValue), digitCount(result));
  const resultVisible = [];
  carry = 0;

  for (let position = 0; position < sumColumnCount; position += 1) {
    const firstDigit = digitAt(firstPartial, position);
    const secondDigit = digitAt(secondPartialValue, position);
    const total = firstDigit + secondDigit + carry;
    const resultDigit = total % 10;
    const nextCarry = Math.floor(total / 10);
    const gridColumn = gridWidth - 1 - position;
    const place = placeNames[position] || 'valor posicional';
    const placeLabel = place[0].toUpperCase() + place.slice(1);
    const placeArticle = place === 'millares' ? 'los' : 'las';
    const expression = carry
      ? `${formatWholeNumber(firstDigit)} + ${formatWholeNumber(secondDigit)} + ${formatWholeNumber(carry)} = ${formatWholeNumber(total)}`
      : `${formatWholeNumber(firstDigit)} + ${formatWholeNumber(secondDigit)} = ${formatWholeNumber(total)}`;
    const nextCarryEntries = nextCarry && gridColumn > 0
      ? [{ gridColumn: gridColumn - 1, value: nextCarry }]
      : [];
    resultVisible.push(gridColumn);

    steps.push({
      id: `sum-${position}`,
      label: `Paso ${steps.length + 1}`,
      title: `${placeLabel}: ${expression}.`,
      message: nextCarry
        ? `Escribimos ${formatWholeNumber(resultDigit)} en ${placeArticle} ${place}. ${regroupMessage(nextCarry, position)}`
        : `Escribimos ${formatWholeNumber(resultDigit)} en ${placeArticle} ${place}.`,
      activeTopColumn: null,
      activeMultiplierColumn: null,
      activeSumColumn: gridColumn,
      carries: nextCarryEntries,
      showFirstPartial: true,
      firstWrittenZeroColumns: finalFirstZeroColumns,
      showSecondPartial: true,
      showSumLine: true,
      resultVisible: [...resultVisible],
    });
    carry = nextCarry;
  }

  steps.push({
    id: 'result',
    label: 'Resultado',
    title: `Entonces, ${formatWholeNumber(a)} × ${formatWholeNumber(b)} = ${formatWholeNumber(result)}.`,
    message: 'El resultado reúne los dos productos parciales.',
    activeTopColumn: null,
    activeMultiplierColumn: null,
    activeSumColumn: null,
    carries: [],
    showFirstPartial: true,
      firstWrittenZeroColumns: finalFirstZeroColumns,
    showSecondPartial: true,
    showSumLine: true,
    showResult: true,
  });

  return {
    kind: 'planet-five-partial-products',
    a,
    b,
    result,
    units,
    tens,
    firstPartial,
    secondPartialValue,
    gridWidth,
    steps,
  };
}


function buildAdaptivePartialProductsTutorial(problem) {
  const a = Math.max(0, Math.trunc(Number(problem?.a) || 0));
  const b = Math.max(0, Math.trunc(Number(problem?.b) || 0));

  // Planetas 5–7 usan dos cifras abajo y, por tanto, productos parciales.
  // Planeta 8 es mixto: conserva el mismo tutorial correcto para cada tipo
  // real de ejercicio, sin forzar productos parciales cuando no corresponde.
  if (digitCount(b) >= 2) {
    return buildPlanetFivePartialProductsTutorial({ ...problem, a, b });
  }
  if (digitCount(a) === 1) {
    return buildEqualGroupsTutorial({ ...problem, a, b });
  }
  if (digitCount(a) === 2) {
    return buildPlanetTwoColumnMultiplicationTutorial({ ...problem, a, b });
  }
  return buildColumnMultiplicationTutorial({ ...problem, a, b });
}

export function buildMultiplicationWorkedExample(problem, missionNumber = 1) {
  if (missionNumber === 2) {
    return buildPlanetTwoColumnMultiplicationTutorial(problem);
  }
  if (missionNumber >= 3 && missionNumber <= 4) {
    return buildColumnMultiplicationTutorial(problem);
  }
  if (missionNumber >= 5 && missionNumber <= 8) {
    return buildAdaptivePartialProductsTutorial(problem);
  }
  return buildEqualGroupsTutorial(problem);
}
